import { jsx, onConnect } from '@amazon/vinyl-tsx'
import { data, type MutableValue } from '@amazon/vinyl-observable'
import { Modal } from '@/components/Modal'
import { Icon } from '@/components/Icon'
import { ImageView } from '@/components/ImageView'
import { ImagePlaceholder } from '@/components/ImagePlaceholder'
import { ImageSizes } from '@/style/styleCommon'
import { createImageModel } from '@/util/ImageUrl'
import { getLargestImage } from '@/util/imageUtil'
import { ctx } from '@/util/context'
import { toast, toastError } from '@/components/toast'
import { fetchLibraryHome } from '@/library/libraryService'
import { appendTracksToPlaylist } from '@/playlistdetail/playlistDetailService'
import type { PlaylistFields } from '@/playlistdetail/playlistFieldsFrag'
import { getString } from '@/library/i18n/bundle.generated'
import { CreatePlaylistModal } from '@/library/CreatePlaylistModal'

export interface AddToPlaylistModalProps {
    /**
     * Drives the modal's visibility. The modal handles its own
     * close interactions (backdrop / Escape / Cancel button) by
     * flipping this back to false.
     */
    readonly visible: MutableValue<boolean>
    /**
     * The track ASIN to add. Captured at open time so a row that
     * recycles in a virtual list while the modal is up still has a
     * stable target.
     */
    readonly trackId: MutableValue<string | null>
}

/**
 * Add to Playlist modal — mirrors green hornet's AddToPlaylistModal:
 * lists the user's owned playlists most-recently-modified first, click
 * one to append the captured track. A primary "Create New Playlist"
 * row at the top opens the existing CreatePlaylistModal seeded with
 * the track id (TODO once createPlaylist supports trackAsins seed at
 * the API call site — for now the create modal opens unseeded).
 */
export function AddToPlaylistModal(props: AddToPlaylistModalProps) {
    const { errorHandler } = ctx('AddToPlaylistModal')

    const playlists = data<readonly PlaylistFields[]>([])
    const loading = data(false)
    const submitting = data<string | null>(null) // playlistId being saved
    const createModalVisible = data(false)

    function close() {
        if (submitting.value) return
        props.visible.value = false
    }

    function loadPlaylists(): void {
        if (loading.value) return
        loading.value = true
        fetchLibraryHome(50)
            .then((result) => {
                const owned = (result.user?.playlists?.edges ?? []).map(
                    (e): PlaylistFields => e.node
                )
                playlists.value = owned
            })
            .catch((err) => {
                toastError("Couldn't load your playlists")
                errorHandler(err)
            })
            .finally(() => {
                loading.value = false
            })
    }

    function addTo(playlist: PlaylistFields) {
        const trackId = props.trackId.value
        const playlistId = playlist.id
        if (!trackId || !playlistId || submitting.value) return
        submitting.value = playlistId
        appendTracksToPlaylist(playlistId, [trackId])
            .then(() => {
                submitting.value = null
                toast(
                    getString('dmusic_playlist_add_to_playlist_modal_success', {
                        playlistName: playlist.title ?? '',
                    }).value
                )
                props.visible.value = false
            })
            .catch((err) => {
                submitting.value = null
                toastError(getString('dmusic_playlist_add_to_playlist_modal_failed').value)
                errorHandler(err)
            })
    }

    function openCreate() {
        // Reuse the existing Create flow. We don't seed a track id —
        // CreatePlaylistInput accepts trackAsins, but the create modal
        // doesn't expose that field; we'd fold the create+append into
        // a single flow once the modal grows that affordance. For now
        // close this modal and pop the create modal solo.
        props.visible.value = false
        createModalVisible.value = true
    }

    return (
        <div>
            <Modal
                className="addToPlaylistModal"
                visible={props.visible}
                onClose={() => close()}
                aria-label={
                    getString('dmusic_add_to_playlist_modal_close_button_accessibility_label').value
                }
                onConnect={() => {
                    // Refresh on every open so a freshly-created
                    // playlist appears immediately.
                    return props.visible.onData((open) => {
                        if (open) loadPlaylists()
                    })
                }}
            >
                <div className="addToPlaylistModalHeader">
                    <h2 className="addToPlaylistModalTitle">
                        {getString('dmusic_playlist_add_to_playlist_modal_recents')}
                    </h2>
                    <button
                        className="iconButton addToPlaylistModalCloseBtn"
                        aria-label={
                            getString(
                                'dmusic_add_to_playlist_modal_close_button_accessibility_label'
                            ).value
                        }
                        onclick={() => close()}
                    >
                        <Icon icon="cancel" />
                    </button>
                </div>

                <button
                    className="addToPlaylistModalCreateRow"
                    onclick={openCreate}
                    disabled={submitting.map((s) => s !== null)}
                >
                    <span className="addToPlaylistModalCreateIcon">
                        <Icon icon="add" />
                    </span>
                    <span className="addToPlaylistModalCreateLabel">
                        {getString('dmusic_playlist_add_to_playlist_modal_create_new_playlist')}
                    </span>
                </button>

                <div className="addToPlaylistModalList">
                    <PlaylistRows
                        playlists={playlists}
                        loading={loading}
                        submitting={submitting}
                        onSelect={addTo}
                    />
                </div>
            </Modal>
            <CreatePlaylistModal visible={createModalVisible} />
        </div>
    )
}

interface PlaylistRowsProps {
    readonly playlists: MutableValue<readonly PlaylistFields[]>
    readonly loading: MutableValue<boolean>
    readonly submitting: MutableValue<string | null>
    readonly onSelect: (p: PlaylistFields) => void
}

function PlaylistRows(props: PlaylistRowsProps) {
    const view = (<div className="addToPlaylistModalRows" />) as HTMLDivElement

    function renderRow(p: PlaylistFields): HTMLElement {
        const image = getLargestImage(p.images, 'PRIMARY') ?? getLargestImage(p.images)
        const imageModel = createImageModel(image, { scaleToLongest: ImageSizes.SMALL })
        const isSubmitting = props.submitting.map((s) => s === p.id)
        const row = (
            <button
                className="addToPlaylistModalRow"
                onclick={() => props.onSelect(p)}
                disabled={props.submitting.map((s) => s !== null)}
            >
                <ImageView
                    image={imageModel}
                    className="addToPlaylistModalRowThumb"
                    placeholder={<ImagePlaceholder />}
                />
                <div className="addToPlaylistModalRowMeta">
                    <div className="addToPlaylistModalRowTitle">{p.title ?? ''}</div>
                    <div className="addToPlaylistModalRowSubtitle">{p.trackCount ?? 0} songs</div>
                </div>
                <span className="addToPlaylistModalRowSpinner" visible={isSubmitting} />
            </button>
        )
        return row
    }

    onConnect(view, () => {
        return props.playlists.onData((list) => {
            // Replace children — small lists (≤ 50) so a full rebuild
            // on every refresh is cheap and avoids a keyed-diff layer.
            view.replaceChildren(...list.map(renderRow))
        })
    })

    return view
}
